import { useEffect, useState } from "react";

import { ArrowLeftIcon, ChatBubbleLeftRightIcon, EnvelopeIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { DateTime } from "luxon";
import { useNavigate, useParams } from "react-router-dom";

import ClaimPartRequestModal from "../../components/partRequests/ClaimPartRequestModal";
import CreatePartRequestForm from "../../components/partRequests/CreatePartRequestForm";
import MatchedLeads from "../../components/partRequests/MatchedLeads";
import MatchedVehicles from "../../components/partRequests/MatchedVehicles";
import NetworkOffers from "../../components/partRequests/NetworkOffers";
import PartRequestYardNotes from "../../components/partRequests/PartRequestYardNotes";
import { PartRequestNetworkPill, PartRequestSourcePill, PartRequestStatusPill } from "../../components/partRequests/PartRequestPills";
import PublishToNetworkConfirm from "../../components/partRequests/PublishToNetworkConfirm";
import PartProMessageChain from "../../components/partProMessaging/PartProMessageChain";
import Card from "../../components/shared/Card/Card";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import usePartRequests from "../../data/usePartRequests";
import { IPartProConversation } from "../../models/PartProMessage";
import {
  IPartRequest,
  PartRequestContactPreference,
  PartRequestSource,
  PartRequestStatus,
} from "../../models/partRequests/IPartRequest";
import formatDateTime from "../../utils/formatDateTime";
import openInNewTab from "../../utils/openInNewTab";
import { partRequestVehicleLabel } from "../../utils/partRequests";

const secondaryButton = "!bg-white border border-gray-300 !text-gray-700 hover:!bg-gray-50";

// The buyer thread PartPro opened when we confirmed - we are the vendor side of it.
function buyerConversation(request: IPartRequest): IPartProConversation {
  return {
    id: request.conversationId ?? "",
    subject: `Part request: ${request.partWanted}`,
    buyerName: request.customerName,
    role: "VENDOR",
    counterpartName: request.customerName,
    listingId: null,
    listingTitle: null,
    listingTagNumber: null,
    orderId: null,
    orderNumber: null,
    unread: false,
    lastMessageBody: null,
    lastMessageSender: null,
    lastMessageAt: request.claimedOn ?? request.requestedAt,
    partId: request.linkedPartId,
    partTagNumber: request.linkedPartTagNumber,
    partImageUrl: null,
    vehicleId: request.linkedVehicleId,
    vehicleVrm: request.linkedVehicleVrm,
    vehicleDescription: null,
  };
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );
}

export default function ViewPartRequest() {
  const { partRequestId } = useParams();
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { partRequest, partProConnected, update, claim, publishToNetwork, withdrawFromNetwork, close, markFulfilled } = usePartRequests(partRequestId);

  const [editing, setEditing] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [fulfilledOpen, setFulfilledOpen] = useState(false);
  const [chainOpen, setChainOpen] = useState(false);

  const request = partRequest.data?.data;

  useEffect(() => {
    setInfo({
      name: request ? `Part Request: ${request.partWanted}` : "Part Request",
      desc: request ? partRequestVehicleLabel(request) : "",
    });
  }, [request?.id, request?.partWanted]);

  if (partRequest.isLoading) return <LoadingWheel />;

  if (!request) {
    return (
      <Card containerClassName="mt-4">
        <p className="p-4 text-sm text-gray-600">This request could not be found.</p>
      </Card>
    );
  }

  const isPartPro = request.source === PartRequestSource.PartPro;
  const isLive = request.status === PartRequestStatus.Open || request.status === PartRequestStatus.Claimed;
  const hasContact = !!(request.customerName || request.customerEmail || request.customerPhone);
  // Anything that has to talk to PartPro is hidden rather than left to fail when the yard is disconnected.
  const canClaimHere = request.canClaim && (!isPartPro || partProConnected);
  const canMessageBuyer = !!request.conversationId && partProConnected;

  if (editing) {
    return (
      <Card containerClassName="mt-4" title="Edit request">
        <CreatePartRequestForm
          existing={request}
          isLoading={update.isLoading}
          onCancel={() => setEditing(false)}
          onSubmit={(body) => update.mutate({ id: request.id, request: body }, { onSuccess: () => setEditing(false) })}
        />
      </Card>
    );
  }

  return (
    <div className="mt-4 space-y-4">
      <ClaimPartRequestModal
        open={claimOpen}
        setOpen={setClaimOpen}
        request={request}
        isLoading={claim.isLoading}
        onConfirm={(body) => claim.mutate({ id: request.id, request: body }, { onSuccess: () => setClaimOpen(false) })}
      />
      <PublishToNetworkConfirm
        open={publishOpen}
        setOpen={setPublishOpen}
        request={request}
        isLoading={publishToNetwork.isLoading}
        onConfirm={() => publishToNetwork.mutate(request.id, { onSuccess: () => setPublishOpen(false) })}
      />
      <ConfirmModal
        open={closeOpen}
        setOpen={setCloseOpen}
        title="Close this request?"
        message={
          request.isOnNetwork
            ? "The request is withdrawn from the PartPro network as well, so other yards stop seeing it."
            : "The request is hidden from the live list. Nothing is sent to the customer."
        }
        confirmButtonText="Close request"
        isLoading={close.isLoading}
        onConfirm={() => close.mutate(request.id, { onSuccess: () => setCloseOpen(false) })}
      />
      <ConfirmModal
        open={fulfilledOpen}
        setOpen={setFulfilledOpen}
        title="Mark as fulfilled?"
        message={
          "Use this once the customer has the part." +
          (request.isOnNetwork ? " The request is withdrawn from the PartPro network." : "")
        }
        confirmButtonText="Fulfilled"
        isLoading={markFulfilled.isLoading}
        onConfirm={() => markFulfilled.mutate({ id: request.id, partId: request.linkedPartId }, { onSuccess: () => setFulfilledOpen(false) })}
      />
      {canMessageBuyer && (
        <PartProMessageChain open={chainOpen} setOpen={setChainOpen} conversation={buyerConversation(request)} />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button type="button" onClick={() => navigate("/part-requests")} className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeftIcon className="h-4 w-4" /> All part requests
        </button>
        <div className="flex flex-wrap items-center gap-2">
          <PartRequestSourcePill source={request.source} />
          <PartRequestStatusPill status={request.status} />
          <PartRequestNetworkPill request={request} />
        </div>
      </div>

      <Card containerClassName="" title={request.partWanted} subtitle={partRequestVehicleLabel(request)}>
        <div className="p-4">
          {request.details && <p className="whitespace-pre-line text-sm text-gray-700">{request.details}</p>}

          <dl className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            <Field label="Requested">
              {formatDateTime(request.requestedAt, DateTime.DATETIME_SHORT)}
              <span className="block text-xs text-gray-500">{DateTime.fromISO(request.requestedAt).toRelative()}</span>
            </Field>
            <Field label="Category">{request.partCategoryName ?? "—"}</Field>
            <Field label="Registration">{request.vrm ?? "—"}</Field>
            <Field label="Make / model">{[request.make, request.model].filter(Boolean).join(" ") || "—"}</Field>
            <Field label="Year">{request.year ?? "—"}</Field>
            <Field label="Engine code">{request.engineCode ?? "—"}</Field>
          </dl>

          <div className="mt-6 flex flex-wrap gap-2">
            {canClaimHere && (
              <ButtonInput label={isPartPro ? "Claim & message buyer" : "Claim"} classes="" onClick={() => setClaimOpen(true)} />
            )}
            {canMessageBuyer && (
              <ButtonInput label="Messages with buyer" classes={secondaryButton} onClick={() => setChainOpen(true)} />
            )}
            {request.canPublishToNetwork && partProConnected && (
              <ButtonInput label="Fulfil on network" classes={secondaryButton} onClick={() => setPublishOpen(true)} />
            )}
            {request.isOnNetwork && partProConnected && (
              <ButtonInput
                label="Withdraw from network"
                classes={secondaryButton}
                isLoading={withdrawFromNetwork.isLoading}
                onClick={() => withdrawFromNetwork.mutate(request.id)}
              />
            )}
            {!isPartPro && isLive && (
              <ButtonInput label="Edit" classes={secondaryButton} onClick={() => setEditing(true)} />
            )}
            {isLive && (
              <ButtonInput label="Mark fulfilled" classes={secondaryButton} onClick={() => setFulfilledOpen(true)} />
            )}
            {isLive && (
              <ButtonInput label="Close" classes="!bg-white border border-red-300 !text-red-700 hover:!bg-red-50" onClick={() => setCloseOpen(true)} />
            )}
          </div>
          {!partProConnected && (isPartPro || request.isOnNetwork) && (
            <p className="mt-3 text-sm text-yellow-800">
              PartPro is not connected, so this request can't be claimed, messaged or withdrawn from here until it is reconnected.
            </p>
          )}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="In the yard" subtitle="Vehicles in stock that match this request">
          <div className="p-4">
            <MatchedVehicles matches={request.matchedVehicles} />
            {(request.linkedVehicleId || request.linkedPartId) && (
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                {request.linkedVehicleId && (
                  <button type="button" onClick={() => openInNewTab(`/vehicles/${request.linkedVehicleId}`)} className="text-primary-600 hover:underline">
                    Linked vehicle{request.linkedVehicleVrm ? ` · ${request.linkedVehicleVrm}` : ""}
                  </button>
                )}
                {request.linkedPartId && (
                  <button type="button" onClick={() => openInNewTab(`/stock/${request.linkedPartId}`)} className="text-primary-600 hover:underline">
                    Linked part{request.linkedPartTagNumber ? ` · Tag ${request.linkedPartTagNumber}` : ""}
                  </button>
                )}
              </div>
            )}
            {isLive && (
              <div className="mt-6 border-t border-gray-100 pt-4">
                <h4 className="text-xs font-medium uppercase tracking-wide text-gray-500">Coming in on leads</h4>
                <p className="mb-2 text-xs text-gray-500">Unbooked leads from the last 60 days with the same registration, or make and model within a year.</p>
                <MatchedLeads matches={request.matchedLeads} total={request.matchedLeadCount} />
              </div>
            )}
          </div>
        </Card>

        <Card title={isPartPro ? "Buyer" : "Customer"} subtitle={isPartPro && !hasContact ? "Contact details unlock when you claim the request" : undefined}>
          <div className="p-4">
            {hasContact ? (
              <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Name">{request.customerName ?? "—"}</Field>
                <Field label="Prefers">
                  {request.contactPreference === PartRequestContactPreference.Phone ? "Phone call" : "Email"}
                </Field>
                <Field label="Phone">
                  {request.customerPhone ? (
                    <a href={`tel:${request.customerPhone}`} className="inline-flex items-center gap-1 text-primary-600 hover:underline">
                      <PhoneIcon className="h-4 w-4" /> {request.customerPhone}
                    </a>
                  ) : "—"}
                </Field>
                <Field label="Email">
                  {request.customerEmail ? (
                    <a href={`mailto:${request.customerEmail}`} className="inline-flex items-center gap-1 text-primary-600 hover:underline">
                      <EnvelopeIcon className="h-4 w-4" /> {request.customerEmail}
                    </a>
                  ) : "—"}
                </Field>
              </dl>
            ) : (
              <p className="text-sm text-gray-500">
                {isPartPro
                  ? "PartPro keeps the buyer anonymous until a yard confirms it can supply the part."
                  : "No contact details recorded."}
              </p>
            )}

            {request.claimedOn && (
              <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm">
                <p className="font-medium text-gray-900">
                  Claimed {formatDateTime(request.claimedOn, DateTime.DATETIME_SHORT)}
                </p>
                {request.claimMessage && <p className="mt-1 whitespace-pre-line text-gray-700">{request.claimMessage}</p>}
                {canMessageBuyer && (
                  <button type="button" onClick={() => setChainOpen(true)} className="mt-2 inline-flex items-center gap-1 text-primary-600 hover:underline">
                    <ChatBubbleLeftRightIcon className="h-4 w-4" /> Open conversation
                  </button>
                )}
              </div>
            )}
          </div>
        </Card>
      </div>

      <Card title="Yard notes" subtitle="Part number, paint code and notes for whoever picks this up">
        <PartRequestYardNotes request={request} />
      </Card>

      {!isPartPro && (request.isOnNetwork || request.offers.length > 0) && (
        <Card title="PartPro network">
          <div className="p-4">
            {request.networkPublishedOn && (
              <p className="text-xs text-gray-500">
                Sent to the network {formatDateTime(request.networkPublishedOn, DateTime.DATETIME_SHORT)}
                {request.networkClosedOn && ` · withdrawn ${formatDateTime(request.networkClosedOn, DateTime.DATETIME_SHORT)}`}
              </p>
            )}
            <NetworkOffers request={request} partProConnected={partProConnected} />
          </div>
        </Card>
      )}
    </div>
  );
}
